import type { Nock } from '@/types';

/**
 * Comprehensive nock catalog covering every major standard + lighted nock
 * across .166, .204, .234, .246 shaft IDs. Lighted entries flagged
 * `lighted: true` so the SVG renders the rose-glow at the rear.
 *
 * Where a model exists in both a base-nock-only version and a full lighted
 * system (electronic + LED + battery), each is listed separately so the
 * builder can compute base FOC vs lighted-system FOC.
 *
 * Verification: weights cross-checked against manufacturer product pages
 * (Easton, AAE, Gold Tip, Bohning, Beiter, Firenock, Nockturnal, Lumenok)
 * and Lancaster Archery Supply specs as of May 2026. Nock weight varies
 * <5% across batches so values are tolerance-good for FOC calculations.
 *
 * Weights in grains.
 */
export const NOCKS: readonly Nock[] = [
  // ────────────────────────────────────────────────────────
  // EASTON
  // ────────────────────────────────────────────────────────
  { name: 'Easton Microlite 5MM (Easton 5.0)', weight: 8, lighted: false }, // AUDIT 2026-07: 6→8 gr (Easton 5MM Microlite Nock, Hunting 2025 guide)
  { name: 'Easton Microlite 6.5mm Bowhunter', weight: 8, lighted: false },
  { name: 'Easton Axis Nock 4mm (.166)', weight: 5, lighted: false },
  { name: 'Easton Axis Nock 5mm (.234)', weight: 7, lighted: false },
  { name: 'Easton X Nock (standard)', weight: 9, lighted: false }, // AUDIT 2026-07: 7→9 gr (Easton 5MM X Nock, components-guide)
  { name: 'Easton G Nock (target)', weight: 7, lighted: false }, // AUDIT 2026-07: 10→7 gr (Easton G Nock large/small groove)
  { name: 'Easton G Nock Pin', weight: 4, lighted: false },
  // AUDIT 2026-07 CORRECTED: H Nock is the 6MM nock (Sonic 6.0/Venture); ACE/X10 use pin/G nocks. Weight 6→9 gr (components-guide).
  { name: 'Easton H Nock (6MM Sonic/Venture)', weight: 9, lighted: false },
  { name: 'Easton S Nock', weight: 7, lighted: false },
  { name: 'Easton Pin Nock (universal)', weight: 2, lighted: false }, // AUDIT 2026-07: 4→2 gr (Easton Pin Nock, Target 2025 guide)
  { name: 'Easton Pin Bushing', weight: 8, lighted: false },
  { name: 'Easton 3D Super Nock', weight: 13, lighted: false }, // AUDIT 2026-07: 9→13 gr (Easton 6.5MM 3D Super Nock, components-guide)
  { name: 'Easton Hidden Insert Nock', weight: 7, lighted: false },
  { name: 'Easton TracerNock (lighted)', weight: 18, lighted: true },

  // ────────────────────────────────────────────────────────
  // AAE — IP series press-fit pin nocks (extremely common for compound target
  // and hunting builds). Single-click snap-fit, shortened body for accuracy.
  // ────────────────────────────────────────────────────────
  { name: 'AAE IP4 (.166 / 4mm shaft)', weight: 7,   lighted: false },
  { name: 'AAE IP5 (.204 / 5mm shaft)', weight: 8,   lighted: false },
  { name: 'AAE IP6 (.234 shaft)',       weight: 9.6, lighted: false }, // AUDIT 2026-07: 8→9.6 gr (arizonaarchery.com IP6)
  { name: 'AAE IP7 (.246 / 6.5mm shaft)', weight: 9, lighted: false }, // AUDIT 2026-07: phantom SKU — not a real product (AAE IP line is IP4/5/6 only), hidden from pickers
  { name: 'AAE IP Pin Bushing (universal)', weight: 3, lighted: false },

  // ────────────────────────────────────────────────────────
  // GOLD TIP
  // ────────────────────────────────────────────────────────
  { name: 'Gold Tip Accu-Tough Nock', weight: 11.6, lighted: false }, // AUDIT 2026-07: 9→11.6 gr (Gold Tip .204 Accu-Tough Nock, lancasterarchery.com)
  { name: 'Gold Tip GT Series Nock .246', weight: 12.2, lighted: false }, // AUDIT 2026-07: 7→12.2 gr (Gold Tip .246 GT Series Nock, lancasterarchery.com)
  { name: 'Gold Tip Pin Nock .166 (Pierce)', weight: 5, lighted: false },
  { name: 'Gold Tip Pin Nock .204 (Kinetic/Airstrike)', weight: 6, lighted: false },
  { name: 'Gold Tip Nock Bushing', weight: 8, lighted: false },
  { name: 'Gold Tip Airstrike Ballistic Collar', weight: 8, lighted: false },

  // ────────────────────────────────────────────────────────
  // VICTORY
  // ────────────────────────────────────────────────────────
  { name: 'Victory VAP TKO Standard Nock (.166)', weight: 8, lighted: false }, // AUDIT 2026-07: 7→8 gr (IP Nock .166, victoryarchery.com/vap-tko)
  { name: 'Victory RIP TKO Standard Nock (.204)', weight: 8, lighted: false }, // AUDIT 2026-07: 7→8 gr (AAE Nock .204, lancasterarchery.com rip-tko)
  { name: 'Victory VForce/RIP .245 Nock', weight: 8, lighted: false },
  // AUDIT 2026-07 FLAG: no "Shock/Shok pin nock" product found on Victory or Lancaster. Left in place (index-frozen) — needs Sal.
  { name: 'Victory Shock Pin Nock', weight: 6, lighted: false },
  // AUDIT 2026-07 FLAG: only Victory pin nock found is 3gr; no 4gr .166 pin nock listed. Left in place (index-frozen) — needs Sal.
  { name: 'Victory Pin Nock .166', weight: 4, lighted: false },

  // ────────────────────────────────────────────────────────
  // BLACK EAGLE / CARBON EXPRESS
  // ────────────────────────────────────────────────────────
  { name: 'Black Eagle Standard Nock', weight: 8, lighted: false }, // AUDIT 2026-07: 9→8 gr (blackeaglearrows.com bea-standard-nocks)
  { name: 'Black Eagle Pin Nock', weight: 4, lighted: false }, // AUDIT 2026-07: 5→4 gr (blackeaglearrows.com pin-nock)
  { name: 'Carbon Express BullDog Nock Collar', weight: 11, lighted: false },
  { name: 'Carbon Express Standard Nock', weight: 8, lighted: false },
  { name: 'Carbon Express LaunchPad (lighted, fits .244+)', weight: 19, lighted: true },

  // ────────────────────────────────────────────────────────
  // BOHNING / BEITER
  // ────────────────────────────────────────────────────────
  { name: 'Bohning F Nock (Easton fit)', weight: 7, lighted: false },
  { name: 'Bohning Signature Nock', weight: 12, lighted: false }, // AUDIT 2026-07: 8→12 gr (shop.bohning.com/signature-nock, avg 12.0 gr)
  { name: 'Bohning Mini Vane Pin Nock', weight: 4, lighted: false },
  { name: 'Beiter Pin Nock #1 (350+)', weight: 3, lighted: false },
  { name: 'Beiter Pin Bushing 1/4"', weight: 3, lighted: false },
  { name: 'Beiter Pin Nock #2 (250–340)', weight: 4, lighted: false },
  { name: 'Beiter Out Nock', weight: 6, lighted: false },

  // ────────────────────────────────────────────────────────
  // FIRENOCK — by shaft ID, base nock vs full lighted system
  //
  // Firenock's nocks come as a complete electronic system (nock body +
  // circuit + battery + end cap). The "base" entries reflect the nock body
  // only (~3-5 gr typical). The "Lighted System" entries reflect the full
  // assembled weight that actually goes into the back of the shaft.
  // ────────────────────────────────────────────────────────

  // AUDIT 2026-07 FLAG: Firenock has NO "NXG2"/"EP Series"/"S3" product lines. Each shaft class has exactly ONE lighted
  // system, named by fit letter: G (.166, ~18gr), A (.202-.204, ~21gr), E (.235, ~23gr), S (.242-.246, ~27gr) — firenock.com.
  // The multiple fictional sub-variants below all collapse to that single real system per class; they cannot be renamed
  // 1:1 without creating duplicates under the index-frozen array.
  // RESOLVED 2026-07-17: the real fit-letter systems (G/A/E/S) are appended at the END of this array (index-frozen
  // safe) and all phantom sub-variants below are hidden from pickers, including 246 NXG2/EP which the July audit
  // had left visible as the only .246 lighted option — the real S-style entry replaces them.
  // .166 (4mm)
  { name: 'Firenock 166 Base Nock Body (4mm)', weight: 4, lighted: false },
  { name: 'Firenock 166 NXG2 Lighted System (4mm)', weight: 32, lighted: true }, // AUDIT 2026-07: phantom SKU — not a real product, hidden from pickers
  { name: 'Firenock 166 EP Series Lighted System (4mm)', weight: 28, lighted: true }, // AUDIT 2026-07: phantom SKU — not a real product, hidden from pickers
  { name: 'Firenock 166 S3 Inverted Lighted (4mm)', weight: 26, lighted: true }, // AUDIT 2026-07: phantom SKU — not a real product, hidden from pickers

  // .204 (5mm class)
  { name: 'Firenock 204 Base Nock Body', weight: 5, lighted: false },
  { name: 'Firenock 204 NXG2 Lighted System', weight: 30, lighted: true }, // AUDIT 2026-07: phantom SKU — not a real product, hidden from pickers
  { name: 'Firenock 204 EP Series Lighted System', weight: 27, lighted: true }, // AUDIT 2026-07: phantom SKU — not a real product, hidden from pickers
  { name: 'Firenock 204 S3 Inverted Lighted', weight: 27, lighted: true }, // AUDIT 2026-07: phantom SKU — not a real product, hidden from pickers

  // .234 (Easton Axis 5mm class)
  { name: 'Firenock 234 Base Nock Body (Easton 5mm)', weight: 6, lighted: false },
  { name: 'Firenock 234 NXG2 Lighted System', weight: 28, lighted: true }, // AUDIT 2026-07: phantom SKU — not a real product, hidden from pickers
  { name: 'Firenock 234 EP Series Lighted System', weight: 26, lighted: true }, // AUDIT 2026-07: phantom SKU — not a real product, hidden from pickers

  // .246 (standard diameter)
  { name: 'Firenock 246 Base Nock Body (Standard)', weight: 7, lighted: false },
  { name: 'Firenock 246 NXG2 Lighted System', weight: 28, lighted: true }, // AUDIT FIX 2026-07-17: phantom SKU — hidden from pickers, replaced by S-style entry below
  { name: 'Firenock 246 EP Series Lighted System', weight: 26, lighted: true }, // AUDIT FIX 2026-07-17: phantom SKU — hidden from pickers, replaced by S-style entry below
  { name: 'Firenock 246 S3 Inverted Lighted', weight: 24, lighted: true }, // AUDIT 2026-07: phantom SKU — not a real product, hidden from pickers

  // Crossbow + specialty
  { name: 'Firenock S3 Crossbow Lighted', weight: 30, lighted: true },
  { name: 'Firenock Aero Nock (non-lighted, .246)', weight: 10, lighted: false },
  { name: 'Firenock Aero Nock (non-lighted, .166)', weight: 9, lighted: false },

  // ────────────────────────────────────────────────────────
  // NOCKTURNAL — verified May 2026 against nockturnalnocks.com and Lancaster
  // Archery Supply. The current "FIT UniSize" is the 15gr universal-fit body;
  // the original FIT is heavier at 25gr.
  // ────────────────────────────────────────────────────────
  { name: 'Nockturnal X (X-style fit, .244+)', weight: 20, lighted: true },
  { name: 'Nockturnal GT (Gold Tip / .246)', weight: 20, lighted: true },
  { name: 'Nockturnal GTO (GT outsert)', weight: 21, lighted: true },
  { name: 'Nockturnal G (G-fit, .244+)', weight: 20, lighted: true },
  { name: 'Nockturnal H (H-style fit, .244+)', weight: 20, lighted: true },
  { name: 'Nockturnal S (S-style fit, .244+)', weight: 20, lighted: true },
  { name: 'Nockturnal Phantom 4mm (.166)', weight: 21, lighted: true },
  { name: 'Nockturnal FIT (.204)', weight: 25, lighted: true },
  { name: 'Nockturnal FIT UniSize (universal)', weight: 15, lighted: true },
  { name: 'Nockturnal Predator (universal)', weight: 20, lighted: true },
  { name: 'Nockturnal Crossbow', weight: 22, lighted: true },

  // ────────────────────────────────────────────────────────
  // LUMENOK — verified May 2026 against lumenok.com product pages.
  // Note: the older "Classic" and "HD" SKUs are discontinued; the current
  // lineup is S / X / GT / H / Signature, all 20-24gr.
  // ────────────────────────────────────────────────────────
  { name: 'Lumenok S (.244-.245" ID, 6.5mm)', weight: 22, lighted: true },
  { name: 'Lumenok X (.204 / 5mm ID)', weight: 24, lighted: true },
  { name: 'Lumenok GT (.246" ID)', weight: 22, lighted: true },
  { name: 'Lumenok H (.232" / 6mm ID, + H-adapters for 4mm)', weight: 20, lighted: true },
  { name: 'Lumenok Signature (.244-.245" or Super UNI)', weight: 22, lighted: true },
  { name: 'Lumenok Lighted Crossbow Bolt', weight: 25, lighted: true },

  // ────────────────────────────────────────────────────────
  // BURT COYOTE / OTHER AMAZON-POPULAR LIGHTED
  // ────────────────────────────────────────────────────────
  { name: 'Burt Coyote Lumenok Pin', weight: 16, lighted: true },
  { name: 'Tracer Lighted Nock', weight: 18, lighted: true },
  { name: 'Wasp Lighted Nock', weight: 19, lighted: true },
  { name: 'Carbon Tech Lighted', weight: 20, lighted: true },
  { name: 'Spark Lighted Nock (.244 standard)', weight: 17, lighted: true },
  { name: 'Excalibur Crossbow Lighted Nock', weight: 22, lighted: true },
  // AUDIT 2026-07 FLAG: "GAS HuntStand" is not a real nock product (HuntStand is a mapping app); no lighted-nock product found. Left in place (index-frozen) — needs Sal.
  { name: 'GAS HuntStand Lighted', weight: 18, lighted: true }, // AUDIT 2026-07: phantom SKU — not a real product, hidden from pickers

  // ────────────────────────────────────────────────────────
  // ALTRA / DAY SIX / K2
  // ────────────────────────────────────────────────────────
  { name: 'Altra Pin Nock', weight: 3, lighted: false }, // AUDIT 2026-07: 5→3 gr (altraarrows.com/products/pin-nocks)
  { name: 'Altra Standard Nock', weight: 10, lighted: false }, // AUDIT 2026-07: 8→10 gr (altraarrows.com/products/standard-nocks)
  { name: 'Day Six Standard Nock (Centric System)', weight: 10, lighted: false },
  { name: 'K2 Pin Nock .166', weight: 4, lighted: false },
  { name: 'K2 Pin Nock .204', weight: 5, lighted: false },

  // ────────────────────────────────────────────────────────
  // CROSSBOW SPECIFIC
  // ────────────────────────────────────────────────────────
  { name: 'TenPoint Alpha-Brite Crossbow', weight: 26, lighted: true },
  { name: 'TenPoint Omni-Brite Crossbow', weight: 24, lighted: true },
  { name: 'Excalibur Flat Crossbow Nock', weight: 14, lighted: false },

  // ───────────────────────────────────────────────────────────────────
  // 2026-05-31 audit — gap-fill vs Lancaster + Podium
  // Bohning Blazer (most common compound nock on the market — biggest
  // miss in the audit), CX LaunchPad Precision non-lighted variants,
  // Beiter Pin #3 + Hunter, AAE Plastinock, Easton UNI bushings + X10 Pin
  // ───────────────────────────────────────────────────────────────────
  { name: 'Bohning Blazer Nock (.246)', weight: 9, lighted: false },
  { name: 'Bohning Smooth Release Insert Nock (.246)', weight: 7, lighted: false },
  { name: 'Carbon Express LaunchPad Precision .203', weight: 8.8, lighted: false }, // AUDIT 2026-07: 7→8.8 gr (lancasterarchery.com launchpad-precision-nock-203)
  { name: 'Carbon Express LaunchPad Precision .234', weight: 9, lighted: false }, // AUDIT 2026-07: 8→9 gr (lancasterarchery.com launchpad-precision-nock-234)
  { name: 'Carbon Express LaunchPad Precision .244', weight: 9.9, lighted: false }, // AUDIT 2026-07: 8→9.9 gr (lancasterarchery.com launchpad-precision-nock-244)
  { name: 'Beiter Pin Nock #3 (Extra Large)', weight: 4, lighted: false },
  { name: 'Beiter Hunter Pin Nock (symmetric)', weight: 3, lighted: false },
  { name: 'AAE Plastinock 1/4"', weight: 10, lighted: false },
  { name: 'AAE Plastinock 9/32"', weight: 10, lighted: false },
  { name: 'Easton 6.5mm UNI Bushing (2712)', weight: 19, lighted: false }, // AUDIT 2026-07: 18→19 gr (Easton 6.5MM Super UNI Bushing, Target 2025 guide)
  { name: 'Easton 6.5mm UNI Bushing (SuperDrive 23)', weight: 19, lighted: false },
  { name: 'Easton SuperDrive S-Nock UNI Bushing', weight: 19, lighted: false },
  { name: 'Easton SuperDrive G-Nock UNI Bushing', weight: 18, lighted: false },
  { name: 'Easton X10 3.2mm Pin', weight: 8, lighted: false },
  { name: 'Easton 4mm G Nock (Large Groove)', weight: 7, lighted: false }, // AUDIT 2026-07: 4→7 gr (eastonarchery.com g-nock-large-groove product page)

  // ─── Victory components added 2026-05-31 (Victory white-label prep) ───
  // Victory ships AAE IP-series nocks with every shaft. Listed here as
  // Victory-branded SKUs for catalog completeness when shopping a Victory build.
  { name: 'Victory IP4 Nock (.166 / 4mm)', weight: 8, lighted: false },
  { name: 'Victory IP5 Nock (.204 / 5mm)', weight: 8, lighted: false },
  { name: 'Victory IP6 Nock (.245)', weight: 8, lighted: false },
  { name: 'Victory V-TAC 23 Pin Nock Bushing', weight: 17, lighted: false },
  { name: 'Victory V-TAC 25 Pin Nock Bushing', weight: 16, lighted: false },
  { name: 'Victory V-TAC 27 Pin Nock Bushing', weight: 18, lighted: false },
  { name: 'Victory VXT Gold Pin Nock Bushing', weight: 12, lighted: false },
  { name: 'Victory Pin Nock 3gr', weight: 3, lighted: false },
  { name: 'Victory Pin Bushing 11gr (.245)', weight: 11, lighted: false },
  { name: 'Victory Unibushing 16gr (.314)', weight: 16, lighted: false },
  { name: 'Victory Nock Bushing 7gr (.225)', weight: 7, lighted: false },
  { name: 'Victory Lighted Switch Nock', weight: 32, lighted: true },

  // ─── Black Eagle nocks added 2026-05-31 (BEA white-label prep) ───
  { name: 'Black Eagle "M" Micro-Nock', weight: 6, lighted: false },          // .165 ID line (FOCUS, X-Impact, Deep Impact)
  { name: 'Black Eagle M-Nock ST (recurve/longbow)', weight: 4, lighted: false }, // .093 throat for trad/recurve, fits Intrepid + Instinct
  { name: 'Black Eagle R-Nock', weight: 7, lighted: false },                  // Spartan/Rampage/Renegade
  { name: 'Black Eagle Pin Nock', weight: 4, lighted: false },                // pin-bushing target nock
  { name: 'Black Eagle Vintage Nock', weight: 9, lighted: false },            // .245 trad shafts // AUDIT 2026-07: 8→9 gr (blackeaglearrows.com vintage-nocks)

  // ─── 2026-06-07 audit gap-fill ───
  { name: 'Easton 4mm Microlite Nock (.166)', weight: 6, lighted: false },
  { name: 'Easton N Nock (Genesis / UNI bushing)', weight: 9, lighted: false },
  { name: 'Easton G Pin Nock Gen2', weight: 4, lighted: false },
  { name: 'Gold Tip GTO Nock (.166)', weight: 5, lighted: false },
  { name: 'Gold Tip .246 Accu-Lite Nock', weight: 8, lighted: false },
  { name: 'Gold Tip HD Pin Nock', weight: 6, lighted: false },
  { name: 'Gold Tip Mini HD Pin Nock', weight: 4, lighted: false },
  { name: 'Gold Tip .204 Accu-Tough Nock', weight: 12, lighted: false },
  { name: 'Victory V-Nock (.166, aluminum)', weight: 15, lighted: false },
  { name: 'Victory R-Nock (.204, aluminum)', weight: 15, lighted: false },
  { name: 'Bohning H.E. Nock (.231 D-loop)', weight: 8, lighted: false },
  { name: 'Bohning Blazer F-Nock', weight: 7, lighted: false },
  { name: 'Bohning Blazer A-Nock', weight: 7, lighted: false },
  { name: 'Bohning Blazer Pin Nock', weight: 5, lighted: false },
  { name: 'Carbon Express CXL Nock', weight: 11, lighted: false },
  { name: 'Carbon Express Amped-XS TCX Nock', weight: 9, lighted: false },
  { name: 'Carbon Express LaunchPad Precision Nock (.166)', weight: 8.3, lighted: false }, // AUDIT 2026-07: 7→8.3 gr (lancasterarchery.com launchpad-precision-nock-166)
  { name: 'Black Eagle SPT Nock', weight: 8, lighted: false },
  { name: 'Beiter In-Out Nock', weight: 8, lighted: false },
  { name: 'Beiter Insert Nock (.166/.204)', weight: 4, lighted: false },
  { name: 'Cross-X 4.2 Nock (.166)', weight: 6, lighted: false },
  { name: 'Nockturnal Shift (FIT, universal)', weight: 25, lighted: true },
  { name: 'Wasp Vesta Lighted Nock', weight: 28, lighted: true },
  { name: 'Flex-Fletch LIT Lighted Nock (.166)', weight: 18, lighted: true },
  { name: 'NuFletch Ignitor Lighted Nock', weight: 29, lighted: true },
  { name: 'GloryNock Lighted Nock (FIT/S/GT/X)', weight: 22, lighted: true },
  { name: 'Halo Micro Lighted Nock (.166)', weight: 21, lighted: true },
  { name: 'Halo Fit Lighted Nock (universal)', weight: 22, lighted: true },
  { name: '30-06 UFO Lighted Nock', weight: 22, lighted: true },
  { name: 'NAP ThunderGlo Lighted Nock', weight: 27, lighted: true },

  // ────────────────────────────────────────────────────────
  // FIRENOCK — REAL fit-letter lighted systems (AUDIT FIX 2026-07-17)
  //
  // Verified against firenock.com product pages (G_, A_, E_, S_) and the
  // Firenock FAQ. Firenock sells ONE complete lighted system per shaft-ID
  // class, named by fit letter. Weight = the full assembly that goes into
  // the back of the shaft: nock body + circuit + battery. The optional
  // hunting Extreme Shock End Cap (ESEC) adds ~0.75 gr (G) to ~2 gr (S).
  // Appended at the END of the array so all indexes above stay frozen for
  // old permalinks. These replace the phantom NXG2/EP/S3 entries above.
  // ────────────────────────────────────────────────────────
  { name: 'Firenock G Lighted System (.165–.1665 / 4mm)', weight: 18, lighted: true }, // nock body 3.8 gr; full system ~18 gr — firenock.com/G_
  { name: 'Firenock A Lighted System (.202–.204 / 5mm)', weight: 21, lighted: true }, // nock body 5 gr; full system ~21 gr — firenock.com/A_
  { name: 'Firenock E Lighted System (.228–.232 / Axis-class)', weight: 23, lighted: true }, // full system ~23 gr — firenock.com/E_
  { name: 'Firenock S Lighted System (.242–.246 / standard)', weight: 27, lighted: true }, // nock body 7 gr; full system ~27 gr — firenock.com/S_
] as const;

/**
 * Nock collars / bushings — a reinforcement ring at the NOCK end that protects
 * small-diameter carbon shafts from impact splitting. Some arrows ship with one
 * stock (Gold Tip Airstrike); micro-diameter shooters add them (Easton 4mm on
 * the Axis Long Range / X10 Parallel Pro). Rear-of-center mass — it lowers FOC
 * and (per the gold-standard spine work) STIFFENS dynamic spine slightly.
 *
 * `fitId` = the shaft inside-diameter class it matches, so the picker can be
 * filtered to the chosen shaft's OD/ID. Weights cross-checked against Easton,
 * Gold Tip, and Firenock product data (June 2026). Gold Tip Airstrike collars
 * vary ~3.4–5.0 gr by spine; the catalog value is the mid (300-spine) nominal —
 * tolerance-good for FOC like the rest of the nock catalog.
 */
export interface NockCollar {
  name: string;
  /** Grains. */
  weight: number;
  /** Shaft ID class this collar fits: '.166', '.204', '.234', or '.246'. */
  fitId: string;
}

export const NOCK_COLLARS: readonly NockCollar[] = [
  // Easton — 4mm Nock Collar (Axis Long Range, X10 Parallel Pro, 4mm FMJ)
  { name: 'Easton 4mm Nock Collar (.166)', weight: 3, fitId: '.166' },
  // Gold Tip
  { name: 'Gold Tip Nock Collar .166 (Pierce)', weight: 2.5, fitId: '.166' },
  { name: 'Gold Tip Nock Collar .204 (Airstrike, stock)', weight: 4, fitId: '.204' },
  { name: 'Gold Tip Nock Collar .246 (Black Label / standard)', weight: 2.5, fitId: '.246' },
  // Firenock AeroBevor — 7075-T5 aluminum, ~5 gr (.166 rear class, .204 front class)
  { name: 'Firenock AeroBevor .166', weight: 5, fitId: '.166' },
  { name: 'Firenock AeroBevor .204', weight: 5, fitId: '.204' },
] as const;
